import * as THREE from 'three';
import { BLACK, BONE, DSTEEL, basic, cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Ogro de sombra (docs/CRIATURAS.md §2): the era I «small giant». A hunched brute of smoky hide split by ember
 * cracks, a small head thrust forward under a hump of shoulder, a tusked underbite, forearms thicker than its
 * thighs and a gnarled trunk bound in iron dragged in the right fist. Opaque shared materials; the ember is its
 * one self-lit material and the smoke its one translucent one, plus a few glow sprites.
 */

const UP = V(0, 1, 0);

/** Where the club's grip sits in the right fist, where its axis points at rest (down and back: it drags) and its length. */
export const OGRE = {
  grip: V(0.68, 0.56, 0.6),
  dir: V(0.62, -0.25, -0.73).normalize(),
  len: 1.45,
};

/**
 * Bones: hips (pelvis, belt; the loincloth flap on its own `loin` pivot), chest above them (the torso bends at the
 * small of the back) carrying the head (jaw hinged under it) and both arms; the forearms bend at the elbow, the
 * club pivots in the right fist and a broken chain swings from the left wrist; the legs bend at the knee.
 */
export const ogre = (o?: BaseOpts): Model => {
  const c = begin(o, 0.85, 41);
  const { g, R, rng } = c;
  const hide = std('#2e2b36', { roughness: 0.7, flatShading: true, bumpScale: 0.05 });
  const hideD = std('#1b1920', { roughness: 0.8, flatShading: true, bumpScale: 0.06 });
  const plate = metal('#34353b', { roughness: 0.55, bumpScale: 0.06 });
  const strap = leather('#2a201c');
  const rag = cloth('#1c1a1e', { side: THREE.DoubleSide, bumpScale: 0.04 });
  const wood = std('#2f2620', { roughness: 0.95, flatShading: true, bumpScale: 0.07 });
  const tusk = std('#c4beb0', { roughness: 0.65 });
  const ember = emis('#ff8a6e', '#ff4f3a', 1.7);

  const SH = [-1, 1].map((k) => V(k * 0.56, 1.6, 0.24));
  const EL = [-1, 1].map((k) => V(k * 0.74, 1.12, 0.36));
  const WR = [-1, 1].map((k) => V(k * 0.68, 0.7, 0.54));
  const hipsJ = joint(g, V(0, 0.95, 0), 'hips');
  const chestJ = joint(hipsJ.abs, V(0, 1.1, 0.06), 'chest');
  const ch = chestJ.abs;
  const headJ = joint(ch, V(0, 1.56, 0.5), 'head');
  const jawJ = joint(headJ.abs, V(0, 1.52, 0.6), 'jaw');
  const loinJ = joint(hipsJ.abs, V(0, 0.94, 0.3), 'loin');
  const armJ = [-1, 1].map((k, i) => joint(ch, SH[i]!, k < 0 ? 'armL' : 'armR'));
  const foreJ = [-1, 1].map((k, i) => joint(armJ[i]!.abs, EL[i]!, k < 0 ? 'foreL' : 'foreR'));
  const legJ = [-1, 1].map((k) => joint(g, V(k * 0.28, 0.92, -0.04), k < 0 ? 'legL' : 'legR'));
  const shinJ = [-1, 1].map((k, i) => joint(legJ[i]!.abs, V(k * 0.4, 0.5, 0.1), k < 0 ? 'shinL' : 'shinR'));

  /** A jagged ember seam laid over the hide: doubled, jittered points joined by a thin glowing tube. */
  const crack = (parent: THREE.Object3D, pts: THREE.Vector3[], r = 0.011): void => {
    const jit: THREE.Vector3[] = [];
    pts.forEach((p, i) => {
      const q = pts[i + 1];
      jit.push(p.clone().add(V(R() * 0.03, R() * 0.03, R() * 0.015)));
      if (q) jit.push(p.clone().lerp(q, 0.5).add(V(R() * 0.05, R() * 0.04, R() * 0.015)));
    });
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(jit, false, 'catmullrom', 0.05), jit.length, r, 3), ember, parent);
  };
  /** A lump of muscle: a squashed sphere. */
  const lump = (parent: THREE.Object3D, mat: THREE.Material, r: number, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1, seg = 7): THREE.Mesh => {
    const m = M(new THREE.SphereGeometry(r, seg, Math.max(5, seg - 3)), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    return m;
  };

  // Legs: short bowed pillars, knees out under the weight, on broad splayed feet with black claws.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const th = legJ[i]!.abs, sh = shinJ[i]!.abs;
    const hip = V(k * 0.28, 0.92, -0.04), knee = V(k * 0.4, 0.5, 0.1), ank = V(k * 0.36, 0.12, -0.02);
    limb(th, hip, knee, 0.21, 0.15, hideD, 7);
    lump(th, hideD, 0.18, k * 0.34, 0.74, 0.04, 1, 1.35, 1.05);
    lump(sh, hideD, 0.13, knee.x, knee.y, knee.z + 0.02, 1, 1, 1, 6);
    limb(sh, knee, ank, 0.15, 0.11, hideD, 7);
    lump(sh, hideD, 0.12, k * 0.39, 0.34, -0.03, 1, 1.5, 1, 6);
    const foot = lump(sh, hideD, 0.16, k * 0.37, 0.07, 0.07, 1, 0.5, 1.45, 7);
    foot.rotation.y = k * 0.15;
    for (let f = 0; f < 3; f++) {
      const cl = M(new THREE.ConeGeometry(0.03, 0.1, 4), BLACK, sh);
      cl.position.set(k * 0.37 + (f - 1) * 0.08 + k * 0.03, 0.04, 0.29 - Math.abs(f - 1) * 0.03);
      cl.rotation.x = Math.PI / 2 + 0.3;
    }
    // An iron ring round the left ankle: it was chained once.
    if (k < 0) {
      const ring = M(new THREE.TorusGeometry(0.14, 0.028, 3, 8), DSTEEL, sh);
      ring.position.set(k * 0.37, 0.2, -0.02);
      ring.rotation.x = Math.PI / 2 + 0.15;
    }
    crack(th, [V(k * 0.24, 0.86, 0.15), V(k * 0.34, 0.72, 0.21), V(k * 0.4, 0.58, 0.22)], 0.009);
  }

  // Pelvis, a crude belt and a ragged loincloth.
  const hp = hipsJ.abs;
  lump(hp, hideD, 0.3, 0, 0.98, 0.04, 1.4, 0.75, 1.05, 8);
  const belt = M(new THREE.TorusGeometry(0.4, 0.05, 3, 14), strap, hp);
  belt.scale.set(1.08, 1, 0.92);
  belt.position.set(0, 0.97, 0.06);
  belt.rotation.x = Math.PI / 2 + 0.1;
  M(new THREE.BoxGeometry(0.13, 0.11, 0.05), DSTEEL, hp).position.set(0, 0.95, 0.43);
  /** A hanging rag with a torn hem (its top edge at y). */
  const tatter = (parent: THREE.Object3D, w: number, h: number, x: number, y: number, z: number, rx: number): void => {
    const gm = new THREE.PlaneGeometry(w, h, 4, 2);
    const p = gm.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) if (p.getY(i) < -h / 4) p.setY(i, p.getY(i) + (i % 2 ? 0.1 : rng() * 0.07));
    gm.computeVertexNormals();
    const m = M(gm, rag, parent);
    m.position.set(x, y - h / 2, z);
    m.rotation.x = rx;
  };
  tatter(loinJ.abs, 0.36, 0.48, 0, 0.94, 0.44, -0.1);
  tatter(hp, 0.5, 0.4, 0, 0.94, -0.34, 0.12);
  // A trophy hangs from the belt: a man's skull, black in the sockets.
  const skull = G(hp, -0.42, 0.82, 0.26);
  skull.rotation.set(0.2, -0.5, 0.15);
  lump(skull, BONE, 0.075, 0, 0, 0, 1, 1, 1.1, 6);
  for (const k of [-1, 1]) lump(skull, BLACK, 0.022, k * 0.028, 0.0, 0.065, 1, 1, 0.5, 4);
  M(new THREE.BoxGeometry(0.07, 0.035, 0.06), BONE, skull).position.set(0, -0.06, 0.03);
  limb(hp, V(-0.4, 0.98, 0.3), V(-0.42, 0.88, 0.27), 0.008, 0.008, strap, 4);

  // Torso: a barrel of chest pitched forward over a sagging belly, and a hump of shoulder so high that the
  // head hangs below it.
  lump(ch, hide, 0.34, 0, 1.1, 0.2, 1.15, 0.92, 1, 8);
  const torso = lump(ch, hide, 0.42, 0, 1.38, 0.2, 1.3, 1.0, 1.0, 9);
  torso.rotation.x = 0.5;
  const hump = lump(ch, hide, 0.32, 0, 1.68, 0.04, 1.5, 0.8, 1.1, 9);
  hump.rotation.x = 0.4;
  for (const k of [-1, 1]) {
    lump(ch, hide, 0.2, k * 0.2, 1.42, 0.52, 1.1, 0.78, 0.6);
  }
  // Spurs of yellowed bone break out along the spine and over the hump, where the hide has split.
  const SPURS: [number, number, number, number, number][] = [[0, 1.97, 0.02, 0.34, -0.4], [-0.22, 1.94, 0.04, 0.24, -0.3], [0.23, 1.93, 0.0, 0.26, -0.35], [0, 1.86, -0.24, 0.3, -0.95], [-0.15, 1.68, -0.38, 0.22, -1.25], [0.15, 1.66, -0.39, 0.23, -1.25]];
  for (const [x, y, z, h, tilt] of SPURS) {
    const sp = M(new THREE.ConeGeometry(h * 0.22, h, 5), tusk, ch);
    sp.position.set(x, y, z);
    sp.rotation.set(tilt, 0, -x * 1.6);
  }
  // A leather strap across the chest from the left shoulder to the right hip, riveted.
  limb(ch, V(-0.46, 1.66, 0.36), V(0.38, 1.0, 0.46), 0.04, 0.04, strap, 4).scale.set(1.5, 1, 0.6);
  for (let i = 0; i < 4; i++) {
    const rv = M(new THREE.BoxGeometry(0.04, 0.04, 0.03), DSTEEL, ch);
    rv.position.set(-0.34 + i * 0.21, 1.57 - i * 0.17, 0.52 + (i === 1 || i === 2 ? 0.07 : 0));
  }
  // Ember cracks running out from the breastbone, over the belly and down the spine.
  for (let i = 0; i < 3; i++) {
    const a = -0.55 + i * 0.6 + R() * 0.2;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 4; j++) {
      const r = 0.06 + j * 0.1;
      pts.push(V(Math.sin(a) * r * 1.3, 1.32 - Math.cos(a) * r * 0.9 + j * 0.03, 0.6 - j * j * 0.025));
    }
    crack(ch, pts, 0.012);
  }
  for (let i = 0; i < 2; i++) crack(ch, [V(-0.12 + i * 0.24, 1.88, -0.05), V(-0.1 + i * 0.2, 1.66, -0.3), V(-0.13 + i * 0.26, 1.36, -0.24)], 0.011);
  const heart = glowSprite(ch, '#ff4f3a', 1.0, V(0, 1.3, 0.64), 0.3, true);

  // Head: small, thrust forward below the hump; a heavy brow over ember eyes, a squashed nose, torn ears.
  const head = G(headJ.abs, 0, 1.58, 0.62);
  head.scale.setScalar(1.12);
  lump(head, hide, 0.155, 0, 0.02, -0.02, 1.15, 0.9, 1.05, 8);
  const brow = lump(head, hide, 0.13, 0, 0.07, 0.08, 1.45, 0.42, 0.75, 7);
  brow.rotation.x = -0.2;
  lump(head, hideD, 0.055, 0, -0.01, 0.15, 1.3, 0.75, 0.9, 5);
  for (const k of [-1, 1]) {
    lump(head, BLACK, 0.034, k * 0.07, 0.025, 0.12, 1.2, 0.7, 0.6, 5);
    lump(head, ember, 0.016, k * 0.07, 0.025, 0.14, 1.3, 0.8, 0.8, 5);
    glowSprite(head, '#ff4f3a', 0.14, V(k * 0.07, 0.025, 0.16), 0.95);
    const ear = M(new THREE.ConeGeometry(0.045, 0.13, 4), hide, head);
    ear.position.set(k * 0.17, 0.05, -0.05);
    ear.rotation.set(0.3, 0, -k * 1.25);
    ear.scale.z = 0.45;
  }
  // The underbite: a heavy jaw thrust forward past the face, tusks curling up past the lip, a glow in the throat.
  const jaw = G(jawJ.abs, 0, 1.58, 0.62);
  jaw.scale.setScalar(1.12);
  const jb = lump(jaw, hide, 0.15, 0, -0.1, 0.05, 1.1, 0.55, 1.0, 8);
  jb.rotation.x = -0.15;
  for (const k of [-1, 1]) {
    const tk = G(jaw, k * 0.1, -0.06, 0.17);
    tk.rotation.set(-0.3, 0, -k * 0.4);
    const tc = M(new THREE.ConeGeometry(0.03, 0.17, 6), tusk, tk);
    tc.position.y = 0.07;
    tc.rotation.x = -0.25;
    const tooth = M(new THREE.ConeGeometry(0.012, 0.035, 4), tusk, jaw);
    tooth.position.set(k * 0.035, -0.04, 0.19);
  }
  glowSprite(jaw, '#ff4f3a', 0.25, V(0, -0.05, 0.1), 0.5);

  // Arms: one massive tapered limb from the shoulder (bulging at the biceps) armoured with plates of hide gone hard
  // as rock, and forearms that swell to the wrist; fists like rocks.
  const plateR = std('#1b1920', { roughness: 0.75, flatShading: true, bumpScale: 0.08 });
  const fores: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const up = armJ[i]!.abs, fo = foreJ[i]!.abs;
    const sh = SH[i]!, el = EL[i]!, wr = WR[i]!;
    // The upper arm: a cylinder from inside the shoulder to the elbow, its middle rings pushed out into a swell.
    const top = sh.clone().add(V(-k * 0.04, 0.14, -0.02));
    const ug = new THREE.CylinderGeometry(0.29, 0.15, top.distanceTo(el), 8, 3);
    const upos = ug.attributes.position as THREE.BufferAttribute;
    const hl = top.distanceTo(el) / 2;
    for (let v = 0; v < upos.count; v++) {
      const y = upos.getY(v) / hl;
      const n = 1 + 0.14 * Math.max(0, 1 - (y + 0.1) ** 2 * 2.2);
      upos.setXYZ(v, upos.getX(v) * n, upos.getY(v), upos.getZ(v) * n);
    }
    ug.computeVertexNormals();
    const um = M(ug, hide, up);
    um.position.copy(top).lerp(el, 0.5);
    um.quaternion.setFromUnitVectors(UP, new THREE.Vector3().subVectors(top, el).normalize());
    // Plates over the shoulder and the outside of the arm (the left one is under the iron).
    const PL: [number, number, number, number][] = k > 0 ? [[0.17, 0.12, 0.0, 0.5], [0.13, -0.18, 0.05, 0.75]] : [[0.13, -0.18, 0.05, 0.75], [0.11, -0.36, 0.1, 0.9]];
    for (const [r, dy, dz, f] of PL) {
      const rp = M(new THREE.DodecahedronGeometry(r, 0), plateR, up);
      rp.position.set(sh.x + k * (0.12 + 0.06 * f), sh.y + dy, sh.z + dz);
      rp.scale.set(0.55, 1, 1.05);
      rp.rotation.set(R() * 0.6, R() * 0.6, k * 0.35);
    }
    lump(fo, hide, 0.13, el.x, el.y, el.z, 1, 1, 1, 6);
    limb(fo, el, wr, 0.14, 0.2, hide, 8);
    lump(fo, hide, 0.18, k * 0.74, 0.92, 0.44, 1, 1.45, 1.05);
    const fist = lump(fo, hideD, 0.19, k * 0.68, 0.56, 0.58, 1, 0.9, 1.1);
    fist.rotation.x = 0.3;
    for (let j = 0; j < 3; j++) {
      const kn = M(new THREE.BoxGeometry(0.09, 0.09, 0.08), hideD, fo);
      kn.rotation.set(0.5, 0.4 * (j - 1), 0.3);
      kn.position.set(k * 0.68 + (j - 1) * 0.085, 0.47, 0.7);
    }
    crack(fo, [V(k * 0.74, 1.08, 0.48), V(k * 0.81, 0.94, 0.52), V(k * 0.78, 0.8, 0.6), V(k * 0.74, 0.68, 0.66)], 0.01);
    fores.push(fo);
  }
  // Left: a shoulder plate of beaten iron, spiked; a shackle on the wrist.
  const al = armJ[0]!.abs;
  const pl = M(new THREE.SphereGeometry(0.34, 9, 4, 0, Math.PI * 2, 0, Math.PI / 2.4), plate, al);
  pl.position.set(-0.6, 1.66, 0.22);
  pl.rotation.set(0.15, 0, 0.55);
  pl.scale.set(1, 0.7, 1.05);
  for (let i = 0; i < 3; i++) {
    const sp = M(new THREE.ConeGeometry(0.04, 0.2 - i * 0.03, 5), DSTEEL, al);
    sp.position.set(-0.66 - i * 0.04, 1.84 - i * 0.05, 0.06 + i * 0.14);
    sp.rotation.set(0, 0, 0.75);
  }
  const cuff = M(new THREE.CylinderGeometry(0.22, 0.22, 0.13, 10, 1, true), plate, fores[0]!);
  cuff.position.set(-0.69, 0.76, 0.52);
  cuff.rotation.x = 0.4;
  // The broken chain hangs from the shackle on its own pivot (it swings behind every move).
  const chainJ = joint(fores[0]!, V(-0.69, 0.72, 0.4), 'chain');
  for (let i = 0; i < 3; i++) {
    const lk = M(new THREE.TorusGeometry(0.045, 0.014, 3, 5), DSTEEL, chainJ.abs);
    lk.position.set(-0.69, 0.68 - i * 0.068, 0.38 - i * 0.01);
    lk.rotation.set(0, i % 2 ? Math.PI / 2 : 0, 0);
    lk.scale.y = 1.3;
  }
  // Right: a band of iron round the forearm.
  const band = M(new THREE.CylinderGeometry(0.21, 0.21, 0.1, 10, 1, true), plate, fores[1]!);
  band.position.set(0.7, 0.8, 0.5);
  band.rotation.x = 0.4;

  // The club: a gnarled trunk, thick at the head and bound with iron bands set with spikes. Its own bone in the fist.
  const clubJ = joint(fores[1]!, OGRE.grip, 'club');
  const club = G(clubJ.abs, OGRE.grip.x, OGRE.grip.y, OGRE.grip.z);
  club.quaternion.setFromUnitVectors(UP, OGRE.dir);
  const L = OGRE.len;
  const tg = new THREE.CylinderGeometry(0.22, 0.065, L, 8, 5);
  const tp = tg.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < tp.count; i++) {
    const x = tp.getX(i), y = tp.getY(i), z = tp.getZ(i);
    const a = Math.atan2(z, x), h = y / L + 0.5;
    // Knotted: lobes that wander round the trunk and bulge toward the head.
    const n = 1 + (0.1 + 0.12 * h) * Math.sin(a * 3 + h * 5) + 0.06 * Math.sin(a * 5 - h * 9);
    tp.setXYZ(i, x * n, y, z * n);
  }
  tg.computeVertexNormals();
  M(tg, wood, club).position.y = L / 2 - 0.16;
  // Gnarled knots at the head, and broken branch stubs.
  const knot = M(new THREE.DodecahedronGeometry(0.2, 0), wood, club);
  knot.position.set(0.02, L - 0.18, 0);
  knot.scale.set(1.1, 0.75, 1);
  for (const [y, a, s] of [[L - 0.62, 0.4, 0.26], [L - 0.38, 3.0, 0.2]] as const) {
    const stub = M(new THREE.ConeGeometry(0.05, s, 5), wood, club);
    stub.position.set(Math.cos(a) * 0.2, y, Math.sin(a) * 0.2);
    stub.quaternion.setFromUnitVectors(UP, V(Math.cos(a), 0.7, Math.sin(a)).normalize());
  }
  for (const [y, r] of [[L - 0.36, 0.24], [L - 0.74, 0.19]] as const) {
    const b = M(new THREE.TorusGeometry(r, 0.03, 3, 10), DSTEEL, club);
    b.position.y = y;
    b.rotation.x = Math.PI / 2;
    for (let s = 0; s < 5; s++) {
      const a = (s / 5) * Math.PI * 2 + y * 2;
      // Iron spikes heated red at the tips: the club reads at table distance by its glints.
      const d = V(Math.cos(a), 0.15, Math.sin(a)).normalize();
      const sp = M(new THREE.ConeGeometry(0.034, 0.12, 4), DSTEEL, club);
      sp.position.set(Math.cos(a) * (r + 0.05), y, Math.sin(a) * (r + 0.05));
      sp.quaternion.setFromUnitVectors(UP, d);
      const tip = M(new THREE.ConeGeometry(0.017, 0.08, 4), ember, club);
      tip.position.copy(sp.position).addScaledVector(d, 0.09);
      tip.quaternion.copy(sp.quaternion);
    }
    glowSprite(club, '#ff4f3a', 0.75, V(0, y, 0), 0.4);
  }
  // Leather binding the grip, and a glowing split in the wood where the iron bit in.
  M(new THREE.CylinderGeometry(0.085, 0.085, 0.14, 6), strap, club).position.set(0, 0, 0);
  crack(club, [V(0.12, L - 1.0, 0.13), V(0.15, L - 0.8, 0.12), V(0.15, L - 0.55, 0.16), V(0.19, L - 0.25, 0.12)], 0.011);

  // Smoke boiling off the shoulders and the hump, like every nightmare.
  const ribs: Ribbon[] = [];
  const wm = basic('#2b2244', 0.42);
  const SMOKE: [number, number, number][] = [[-0.52, 1.78, 0.18], [0.52, 1.78, 0.18], [0.0, 1.85, -0.2]];
  for (const [x, y, z] of SMOKE) ribs.push(new Ribbon(ch, wisp(x, z, 0.8, 6).map((p) => p.add(V(0, y, 0))), 0.035, wm, rng, 0.06));
  glowSprite(ch, '#6a3a9a', 1.7, V(0, 1.5, 0.0), 0.12);

  // Only the boil here: every move is the clips' (anim/creatures/ogre.ts).
  const t = (n: number): void => {
    for (const r of ribs) r.step();
    heart.material.opacity = 0.22 + ((n * 3) % 7) / 40;
  };
  t(0);
  return done(c, 2.1, t, undefined, bonesOf(c, {
    hips: hipsJ.p, chest: chestJ.p, head: headJ.p, jaw: jawJ.p, loin: loinJ.p,
    armL: armJ[0]!.p, foreL: foreJ[0]!.p, chain: chainJ.p, armR: armJ[1]!.p, foreR: foreJ[1]!.p, club: clubJ.p,
    legL: legJ[0]!.p, shinL: shinJ[0]!.p, legR: legJ[1]!.p, shinR: shinJ[1]!.p,
  }));
};
